import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/shared/ui/badge'
import { Label } from '@/shared/ui/label'
import { NativeSelect } from '@/shared/ui/native-select'
import { Switch } from '@/shared/ui/switch'
import { CopyButton } from '@/shared/components/copy-button'
import { EXAMPLE_LANGUAGES, formatJson, formatRequest } from '../request'
import type { ExampleLanguage } from '../request'
import { CodeBlock } from './code-block'

/**
 * How each request is currently being read.
 *
 * One choice for the whole page rather than one per endpoint: somebody working
 * in Python is working in Python all the way down, and having to say so again
 * at every heading would be the page arguing with them.
 */
export interface ExampleSettings {
  language: ExampleLanguage
  userFieldNames: boolean
}

const LANGUAGE_NAMES: Record<ExampleLanguage, string> = {
  curl: 'cURL',
  http: 'HTTP',
  javascript: 'JavaScript (axios)',
  python: 'Python (requests)',
}

const METHOD_TONE: Record<string, string> = {
  GET: 'bg-success/20 text-foreground',
  POST: 'bg-primary/20 text-foreground',
  PATCH: 'bg-pending/30 text-foreground',
  DELETE: 'bg-destructive/20 text-foreground',
}

/**
 * One documented call: what it is for, what it takes, and what it answers.
 *
 * Two columns on a wide screen and one on a narrow one -- the prose and the
 * example are read together, and stacking them on a phone is the only way both
 * stay legible.
 */
export function Endpoint({
  id,
  title,
  method,
  url,
  description,
  parameters,
  request,
  response,
  filename,
  mapping,
  settings,
  onSettings,
  showUserFieldNames = true,
}: {
  id: string
  title: string
  method?: string
  url: string
  description: ReactNode
  parameters?: ReactNode
  request?: unknown
  response?: unknown
  filename?: string
  mapping?: Record<string, string>
  settings: ExampleSettings
  onSettings: (settings: ExampleSettings) => void
  showUserFieldNames?: boolean
}) {
  const { t } = useTranslation()
  const code = formatRequest(settings.language, {
    ...(method === undefined ? {} : { method }),
    url,
    ...(request === undefined ? {} : { body: request }),
    ...(filename === undefined ? {} : { filename }),
  })

  return (
    <section
      id={id}
      // Scrolled to by the contents list, and the heading must not end up under
      // the sticky header when it gets there.
      className="scroll-mt-20 border-t py-8 lg:grid lg:grid-cols-2 lg:gap-10"
    >
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">{title}</h3>
        <div className="text-muted-foreground space-y-2 text-sm">{description}</div>
        {parameters}
      </div>

      <div className="mt-6 space-y-3 lg:mt-0">
        <div className="flex items-center gap-2 rounded-lg border p-2">
          {method !== undefined && (
            <Badge className={METHOD_TONE[method] ?? ''}>{method}</Badge>
          )}
          <code className="min-w-0 flex-1 truncate font-mono text-xs">{url}</code>
          <CopyButton value={url} label={t('action.copy')} />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Label htmlFor={`${id}-language`} className="sr-only">
              {t('apiDocs.language')}
            </Label>
            <NativeSelect
              id={`${id}-language`}
              className="h-8 w-48"
              value={settings.language}
              onChange={(event) => {
                onSettings({
                  ...settings,
                  language: event.target.value as ExampleLanguage,
                })
              }}
            >
              {EXAMPLE_LANGUAGES.map((one) => (
                <option key={one} value={one}>
                  {LANGUAGE_NAMES[one]}
                </option>
              ))}
            </NativeSelect>
          </div>
          {showUserFieldNames && (
            <div className="flex items-center gap-2">
              <Switch
                id={`${id}-names`}
                size="sm"
                checked={settings.userFieldNames}
                onCheckedChange={(checked) => {
                  onSettings({ ...settings, userFieldNames: checked })
                }}
              />
              <Label htmlFor={`${id}-names`} className="text-sm">
                {t('apiDocs.userFieldNames')}
              </Label>
            </div>
          )}
        </div>

        <div>
          <p className="text-muted-foreground mb-1 text-xs font-medium">
            {t('apiDocs.requestSample')}
          </p>
          <CodeBlock
            code={code}
            {...(mapping === undefined ? {} : { mapping })}
            label={t('apiDocs.requestSample')}
          />
        </div>

        {response !== undefined && (
          <div>
            <p className="text-muted-foreground mb-1 text-xs font-medium">
              {t('apiDocs.responseSample')}
            </p>
            <CodeBlock
              code={formatJson(response)}
              {...(mapping === undefined ? {} : { mapping })}
              label={t('apiDocs.responseSample')}
            />
          </div>
        )}
      </div>
    </section>
  )
}

/** One query, path or body parameter. */
export function Parameter({
  name,
  type,
  optional = true,
  standard,
  children,
}: {
  name: string
  type: string
  optional?: boolean
  standard?: string
  children: ReactNode
}) {
  const { t } = useTranslation()

  return (
    <li className="border-t py-3 first:border-t-0">
      <div className="flex flex-wrap items-baseline gap-2">
        <code className="font-mono text-sm font-medium">{name}</code>
        <span className="text-muted-foreground text-xs">{type}</span>
        {optional ? (
          <span className="text-muted-foreground text-xs">{t('apiDocs.optional')}</span>
        ) : (
          <span className="text-destructive text-xs">{t('apiDocs.required')}</span>
        )}
        {standard !== undefined && (
          <span className="text-muted-foreground text-xs">
            {t('apiDocs.defaultValue', { value: standard })}
          </span>
        )}
      </div>
      <div className="text-muted-foreground mt-1 space-y-2 text-sm">{children}</div>
    </li>
  )
}

export function Parameters({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h4 className="mb-1 text-sm font-medium">{title}</h4>
      <ul>{children}</ul>
    </div>
  )
}
